/* eslint-disable camelcase */
import React from "react";
import {
	type ClassNames,
	DayPicker,
	type MonthCaptionProps,
	type PropsBase,
	type PropsSingle,
	useDayPicker,
} from "react-day-picker";
import { usePopper } from "react-popper";

import { faArrowLeft, faArrowRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import cx from "classnames";
import { getDay, getYear, isThisYear } from "date-fns";

import useFirstWeekday from "~/calendar/hooks/useFirstWeekday";
import { useBlur } from "~/lib/hooks";
import { Button } from "~/ui";

import IconButton from "../legacy/IconButton";

import styles from "./DateSelector.module.css";

const WEEKDAYS_SHORT = ["S", "M", "T", "W", "T", "F", "S"];

interface DateSelectorProps
	extends React.PropsWithChildren<Omit<DateSelectorDayPickerProps, "selected" | "onSelect" | "setToToday">> {
	triggerWrapperClassName?: string;
	disabled?: boolean;
	date: Date;
	setDate: (date: Date) => void;
}

export default function DateSelector({
	triggerWrapperClassName,
	children,
	disabled,
	date,
	setDate,
	renderTodayButton,
	...rest
}: DateSelectorProps): JSX.Element {
	const [isOpen, setIsOpen] = React.useState(false);

	const handleDayClick = React.useCallback<NonNullable<PropsSingle["onSelect"]>>(
		(_, selectedDay, modifiers) => {
			if (modifiers.disabled) return;

			setDate(selectedDay);
			setIsOpen(false);
		},
		[setDate],
	);

	const targetFromBlurClick = React.useRef<HTMLElement | null>(null);
	const handleOpenDateSelector = React.useCallback(
		(e?: React.MouseEvent) => {
			if (e && e.target === targetFromBlurClick.current) {
				targetFromBlurClick.current = null;
				return;
			}

			if (!disabled) setIsOpen(prev => !prev);
		},
		[disabled],
	);
	React.useEffect(() => {
		if (disabled) setIsOpen(false);
	}, [disabled]);

	const propsForTodayButton = React.useMemo(
		() =>
			renderTodayButton
				? {
						renderTodayButton: true,
						setToToday: () => {
							setDate(new Date());
							setIsOpen(false);
						},
					}
				: {},
		[renderTodayButton, setDate],
	);

	const triggerRef = React.useRef<HTMLDivElement | null>(null);
	const [popperElement, setPopperElement] = React.useState<HTMLDivElement | null>(null);

	const blurRef = React.useRef<HTMLDivElement>(null);

	useBlur(
		blurRef,
		React.useCallback(
			e => {
				if (isOpen) {
					handleOpenDateSelector();
					targetFromBlurClick.current = e.target as HTMLElement;
				}
			},
			[handleOpenDateSelector, isOpen],
		),
		true,
	);

	const { styles: popperStyles, attributes } = usePopper(triggerRef.current, popperElement, {
		placement: "bottom",
		modifiers: [
			{
				name: "offset",
				options: {
					offset: [0, 3],
				},
			},
		],
	});

	return (
		<>
			<div ref={triggerRef} onClick={handleOpenDateSelector} className={triggerWrapperClassName}>
				{children}
			</div>
			{isOpen && (
				<div ref={blurRef}>
					<div ref={setPopperElement} style={popperStyles.popper} className={styles.wrapper} {...attributes.popper}>
						<DateSelectorDayPicker
							{...rest}
							{...propsForTodayButton}
							defaultMonth={date}
							selected={date}
							onSelect={handleDayClick}
						/>
					</div>
				</div>
			)}
		</>
	);
}

function NavHeader({ calendarMonth }: MonthCaptionProps) {
	const { goToMonth, previousMonth, nextMonth } = useDayPicker();

	const label = React.useMemo(
		() =>
			calendarMonth.date.toLocaleString(undefined, {
				month: "long",
				...(!isThisYear(calendarMonth.date) ? { year: "numeric" } : {}),
			}),
		[calendarMonth],
	);

	const navigate = React.useCallback(
		(to: "prev" | "next") => () => {
			switch (to) {
				case "prev": {
					if (previousMonth) goToMonth(previousMonth);
					break;
				}
				case "next": {
					if (nextMonth) goToMonth(nextMonth);
					break;
				}
			}
		},
		[goToMonth, previousMonth, nextMonth],
	);

	return (
		<div className={styles.monthAndNavigation}>
			<div className={styles.monthLabel}>{label}</div>
			<div>
				<IconButton icon={faArrowLeft} className={styles.buttonPrevious} onClick={navigate("prev")} />
				<IconButton icon={faArrowRight} className={styles.buttonNext} onClick={navigate("next")} />
			</div>
		</div>
	);
}

export interface DateSelectorDayPickerProps extends Omit<PropsBase & PropsSingle, "mode"> {
	setToToday?: () => void;
	renderTodayButton?: boolean;
}

export function DateSelectorDayPicker({ renderTodayButton, setToToday, ...rest }: DateSelectorDayPickerProps) {
	const customClassNames: Partial<ClassNames> = React.useMemo(() => {
		const classNames: Partial<ClassNames> & {
			[key: string]: string | undefined;
		} = {
			root: styles.root,
			months: styles.fullWidth,
			month: styles.fullWidth,
			month_caption: styles.monthAndNavigation,
			caption_label: styles.monthLabel, // ??
			button_next: styles.buttonNext,
			button_previous: styles.buttonPrevious,
			month_grid: styles.monthGrid,
			weekday: styles.weekdayLabel,
			day: styles.dayCell,
			day_button: styles.dayButton,
			outside: styles.dayOutsideOfMonth,
			selected: styles.daySelected,
			today: styles.dayToday,
			footer: styles.todayButtonContainer,
		};

		Object.entries(rest.classNames || {}).forEach(
			([element, className]) => (classNames[element] = cx(classNames[element], className)),
		);

		return classNames;
	}, [rest.classNames]);

	const customFormatters = React.useMemo<PropsBase["formatters"]>(
		() => ({
			formatWeekdayName: date => WEEKDAYS_SHORT[getDay(date)],
			formatCaption: date =>
				`${date.toLocaleString(undefined, { month: "long" })} ${!isThisYear(date) ? getYear(date) : ""}`,
			...rest.formatters,
		}),
		[rest.formatters],
	);

	const customComponents = React.useMemo<PropsBase["components"]>(
		() => ({
			MonthCaption: NavHeader,
			...rest.components,
		}),
		[rest.components],
	);

	const customFooter = React.useMemo<PropsBase["footer"]>(() => {
		if (!renderTodayButton) return;

		return (
			<Button variant="secondary" size="slim" onClick={setToToday}>
				Today
			</Button>
		);
	}, [renderTodayButton, setToToday]);

	return (
		<DayPicker
			{...rest}
			mode="single"
			hideNavigation
			classNames={customClassNames}
			showOutsideDays
			weekStartsOn={useFirstWeekday()}
			formatters={customFormatters}
			components={customComponents}
			footer={customFooter}
		/>
	);
}
